import { useEffect } from 'react';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

interface LoaderState {
  activeLoaders: Array<string>;
  displayLoader: (componentKey: string, isLoading: boolean) => void;
  loaderVisible: boolean;
  registeredLoaders: Array<string>;
  registerLoader: (componentKey: string) => void;
}

export const useGlobalLoaderStore = create(
  subscribeWithSelector<LoaderState>((set, get) => ({
    activeLoaders: [],
    displayLoader: (componentKey, isLoading) => {
      const registeredLoaders = get().registeredLoaders;
      if (!registeredLoaders.includes(componentKey))
        throw new Error(`${componentKey} component is not registered!`);
      const activeLoaders = get().activeLoaders;
      if (isLoading && !activeLoaders.includes(componentKey)) {
        return set((state) => ({
          activeLoaders: [...state.activeLoaders, componentKey],
        }));
      }
      return set((state) => ({
        activeLoaders: state.activeLoaders.filter((k) => k !== componentKey),
      }));
    },
    loaderVisible: false,
    registeredLoaders: [],
    registerLoader: (componentKey) => {
      const registeredLoaders = get().registeredLoaders;
      if (registeredLoaders.includes(componentKey)) return;
      return set((state) => ({
        registeredLoaders: [...state.registeredLoaders, componentKey],
      }));
    },
  }))
);

// set `loaderVisible` anytime `activeLoaders` changes
useGlobalLoaderStore.subscribe(
  (state) => Boolean(state.activeLoaders.length),
  (loaderVisible) => {
    useGlobalLoaderStore.setState({ loaderVisible });
  }
);

// debug loaders
/*
useGlobalLoaderStore.subscribe(state => {
  console.log('Active loaders:', state.activeLoaders);
  console.log('Registered loaders:', state.registeredLoaders);
});
*/

export const useLoaderEffect = (componentKey: string, isLoading: boolean) => {
  const store = useGlobalLoaderStore();
  const { displayLoader } = store;

  useEffect(() => {
    if (isLoading) {
      displayLoader(componentKey, isLoading);
      return () => displayLoader(componentKey, false);
    }
  }, [componentKey, isLoading, displayLoader]);

  return null;
};

export const registerLoader = (componentKey: string) => {
  const loaderStore = useGlobalLoaderStore.getState();
  loaderStore.registerLoader(componentKey);
};
